import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  Checkbox,
} from "@mui/material";
import {
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import EmailPreviewDialog from "../Components/EmailPreviewDialog";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import {
  GET_ATTENDEES,
  CREATE_ATTENDEE,
  UPLOAD_ATTENDEES,
  GET_ATTENDEE_IMPORTS,
  GET_ATTENDEE_IMPORT_DETAIL,
  GET_EMAIL_TEMPLATES,
  SEND_BULK_EMAILS,
} from "../graphql/queries";

const AttendeeManagementPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [createDialog, setCreateDialog] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    shopName: "",
    shopLocation: "",
    shopContact: "",
  });
  const [csvFile, setCsvFile] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadStatusModal, setUploadStatusModal] = useState({
    open: false,
    importId: null,
    successCount: 0,
    failedCount: 0,
    totalRecords: 0,
    message: "",
  });
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailFromUploadOpen, setEmailFromUploadOpen] = useState(false);
  const [uploadSuccessRowsForEmail, setUploadSuccessRowsForEmail] = useState([]);
  const [failedRowsDetail, setFailedRowsDetail] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const {
    data: attendeesData,
    loading: attendeesLoading,
    refetch: refetchAttendees,
  } = useQuery(GET_ATTENDEES);
  const {
    data: importsData,
    loading: importsLoading,
    refetch: refetchImports,
  } = useQuery(GET_ATTENDEE_IMPORTS);
  const { data: templatesData } = useQuery(GET_EMAIL_TEMPLATES, {
    variables: { templateType: "attendee_welcome" },
  });

  const [createAttendee] = useMutation(CREATE_ATTENDEE);
  const [uploadAttendees] = useMutation(UPLOAD_ATTENDEES);
  const [sendBulkEmails] = useMutation(SEND_BULK_EMAILS);
  const [fetchImportDetail] = useLazyQuery(GET_ATTENDEE_IMPORT_DETAIL);

  const attendees = attendeesData?.getAttendees || [];
  const imports = importsData?.getAttendeeImports || [];

  const handleCreate = async () => {
    try {
      await createAttendee({ variables: formData });
      setNotification({
        open: true,
        message: "Attendee created successfully",
        severity: "success",
      });
      setCreateDialog(false);
      setFormData({
        email: "",
        name: "",
        password: "",
        shopName: "",
        shopLocation: "",
        shopContact: "",
      });
      refetchAttendees();
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || "Failed to create attendee",
        severity: "error",
      });
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setCsvFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setCsvText(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvText) {
      setNotification({
        open: true,
        message: "Please select a CSV file",
        severity: "error",
      });
      return;
    }

    const { missing } = validateCsvColumns(csvText);
    if (missing.length > 0) {
      const msg = missing
        .map((c) => `${c} is not available`)
        .join(". ");
      setNotification({
        open: true,
        message: `${msg}. Expected format: ${SAMPLE_CSV_HEADER}`,
        severity: "error",
      });
      return;
    }

    try {
      const result = await uploadAttendees({
        variables: {
          csvData: csvText,
          fileName: csvFile?.name || undefined,
        },
      });
      const raw = result.data.uploadAttendees;
      let payload = { importId: null, successCount: 0, failedCount: 0, totalRecords: 0, message: raw };
      try {
        payload = JSON.parse(raw);
      } catch (_) {}
      setUploadStatusModal({
        open: true,
        importId: payload.importId,
        successCount: payload.successCount ?? 0,
        failedCount: payload.failedCount ?? 0,
        totalRecords: payload.totalRecords ?? 0,
        message: payload.message || raw,
      });
      setUploadDialog(false);
      setCsvFile(null);
      setCsvText("");
      refetchAttendees();
      refetchImports();
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || "Failed to upload attendees",
        severity: "error",
      });
    }
  };

  const REQUIRED_CSV_COLUMNS = [
    "email",
    "name",
    "password",
    "shop_name",
  ];
  const OPTIONAL_CSV_COLUMNS = ["shop_location", "shop_contact"];
  const SAMPLE_CSV_HEADER =
    "email,name,password,shop_name,shop_location,shop_contact";

  const parseCsvHeaders = (text) => {
    if (!text?.trim()) return [];
    const firstLine = text.split(/\r?\n/)[0];
    return firstLine
      .split(",")
      .map((h) => h.trim().replace(/^"|"$/g, "").toLowerCase());
  };

  const validateCsvColumns = (text) => {
    const headers = parseCsvHeaders(text);
    const missing = REQUIRED_CSV_COLUMNS.filter((col) => !headers.includes(col));
    return { headers, missing };
  };

  const downloadCsvFromRows = (rows, filename) => {
    if (!rows?.length) return;
    const headers = Object.keys(rows[0]);
    const headerLine = headers.join(",");
    const dataLines = rows.map((r) =>
      headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","),
    );
    const csv = [headerLine, ...dataLines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadSuccessList = async () => {
    if (!uploadStatusModal.importId || uploadStatusModal.successCount === 0) return;
    try {
      const { data } = await fetchImportDetail({
        variables: { importId: uploadStatusModal.importId },
      });
      const detail = data?.getAttendeeImportDetail;
      if (!detail?.successRows) return;
      const rows = JSON.parse(detail.successRows);
      downloadCsvFromRows(rows, `import_success_${uploadStatusModal.importId}.csv`);
    } catch (e) {
      setNotification({ open: true, message: e.message || "Failed to download", severity: "error" });
    }
  };

  // Fetch failed rows detail when upload status modal opens with failures
  React.useEffect(() => {
    if (
      uploadStatusModal.open &&
      uploadStatusModal.importId &&
      uploadStatusModal.failedCount > 0
    ) {
      fetchImportDetail({
        variables: { importId: uploadStatusModal.importId },
      }).then(({ data }) => {
        const detail = data?.getAttendeeImportDetail;
        if (detail?.failedRows) {
          try {
            const rows = JSON.parse(detail.failedRows);
            setFailedRowsDetail(Array.isArray(rows) ? rows : []);
          } catch {
            setFailedRowsDetail([]);
          }
        } else {
          setFailedRowsDetail([]);
        }
      });
    } else {
      setFailedRowsDetail([]);
    }
  }, [
    uploadStatusModal.open,
    uploadStatusModal.importId,
    uploadStatusModal.failedCount,
  ]);

  const handleConfirmSendEmailToUploaded = async () => {
    if (
      !uploadStatusModal.importId ||
      uploadStatusModal.successCount === 0 ||
      !templatesData?.getEmailTemplates?.length
    ) {
      setNotification({
        open: true,
        message: uploadStatusModal.successCount === 0
          ? "No successfully imported users to email"
          : "Please create an attendee welcome email template first",
        severity: "error",
      });
      return;
    }
    try {
      const { data } = await fetchImportDetail({
        variables: { importId: uploadStatusModal.importId },
      });
      const detail = data?.getAttendeeImportDetail;
      const rows = detail?.successRows ? JSON.parse(detail.successRows) : [];
      if (rows.length === 0) {
        setNotification({
          open: true,
          message: "No attendee IDs found in import",
          severity: "error",
        });
        return;
      }
      setUploadSuccessRowsForEmail(rows);
      setEmailFromUploadOpen(true);
    } catch (e) {
      setNotification({
        open: true,
        message: e.message || "Failed to load import details",
        severity: "error",
      });
    }
  };

  const handleSendEmailsFromUploadPreview = async () => {
    const ids = uploadSuccessRowsForEmail.map((r) => r.id).filter(Boolean);
    if (ids.length === 0) throw new Error("No attendees to email");
    const result = await sendBulkEmails({
      variables: {
        attendeeIds: ids,
        templateType: "attendee_welcome",
      },
    });
    setNotification({
      open: true,
      message: result.data.sendBulkEmails,
      severity: "success",
    });
    setEmailFromUploadOpen(false);
    setUploadStatusModal((s) => ({ ...s, open: false }));
    refetchAttendees();
    refetchImports();
    return result.data.sendBulkEmails;
  };

  const handleDownloadFailedList = async () => {
    if (!uploadStatusModal.importId || uploadStatusModal.failedCount === 0) return;
    try {
      const { data } = await fetchImportDetail({
        variables: { importId: uploadStatusModal.importId },
      });
      const detail = data?.getAttendeeImportDetail;
      if (!detail?.failedRows) return;
      const rows = JSON.parse(detail.failedRows);
      downloadCsvFromRows(rows, `import_failed_${uploadStatusModal.importId}.csv`);
    } catch (e) {
      setNotification({ open: true, message: e.message || "Failed to download", severity: "error" });
    }
  };

  const downloadSampleCsv = () => {
    const sample = `${SAMPLE_CSV_HEADER}\njohn@example.com,John Doe,password123,Shop A,Location A,+968 1234 5678\njane@example.com,Jane Smith,password456,Shop B,Location B,+968 9876 5432`;
    const blob = new Blob([sample], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "attendee_sample.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const attendeeWelcomeTemplate = templatesData?.getEmailTemplates?.[0] || null;
  const selectedAttendeeRows = attendees.filter((a) =>
    selectedAttendees.includes(a.id),
  );

  const handleSendEmailsClick = () => {
    if (selectedAttendees.length < 1) {
      setNotification({
        open: true,
        message: "Please select at least one attendee to send email",
        severity: "error",
      });
      return;
    }
    if (!attendeeWelcomeTemplate) {
      setNotification({
        open: true,
        message: "Please create an attendee welcome email template first",
        severity: "error",
      });
      return;
    }
    setEmailPreviewOpen(true);
  };

  const handleSendEmailsFromPreview = async () => {
    const result = await sendBulkEmails({
      variables: {
        attendeeIds: selectedAttendees,
        templateType: "attendee_welcome",
      },
    });
    setNotification({
      open: true,
      message: result.data.sendBulkEmails,
      severity: "success",
    });
    setSelectedAttendees([]);
    setEmailPreviewOpen(false);
    return result.data.sendBulkEmails;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Attendee/Shop Management</Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog(true)}
            sx={{ mr: 1 }}
          >
            Create Attendee/Shop
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialog(true)}
          >
            Upload CSV
          </Button>
        </Box>
      </Box>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Shops" />
        <Tab label="Import History" />
      </Tabs>

      {tabValue === 0 && (
        <Box>
          {selectedAttendees.length > 0 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EmailIcon />}
              onClick={handleSendEmailsClick}
              sx={{ mb: 2 }}
            >
              Send Email ({selectedAttendees.length})
            </Button>
          )}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedAttendees.length > 0 &&
                        selectedAttendees.length < attendees.length
                      }
                      checked={
                        attendees.length > 0 &&
                        selectedAttendees.length === attendees.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedAttendees(attendees.map((a) => a.id));
                        } else {
                          setSelectedAttendees([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Shop Name</TableCell>
                <TableCell>Shop Location</TableCell>
                <TableCell>Shop Contact</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attendeesLoading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : attendees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No attendees found
                  </TableCell>
                </TableRow>
              ) : (
                attendees.map((attendee) => (
                  <TableRow
                    key={attendee.id}
                    selected={selectedAttendees.includes(attendee.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedAttendees.includes(attendee.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          setSelectedAttendees((prev) =>
                            prev.includes(attendee.id)
                              ? prev.filter((id) => id !== attendee.id)
                              : [...prev, attendee.id],
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell>{attendee.email}</TableCell>
                    <TableCell>{attendee.name}</TableCell>
                    <TableCell>{attendee.shopName || "-"}</TableCell>
                    <TableCell>{attendee.shopLocation || "-"}</TableCell>
                    <TableCell>{attendee.shopContact || "-"}</TableCell>
                    <TableCell>
                      {new Date(attendee.created).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      )}

      {tabValue === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>File Name</TableCell>
                <TableCell>Total Records</TableCell>
                <TableCell>Success</TableCell>
                <TableCell>Failed</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {importsLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : imports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No import history
                  </TableCell>
                </TableRow>
              ) : (
                imports.map((importRecord) => (
                  <TableRow
                    key={importRecord.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      setUploadStatusModal({
                        open: true,
                        importId: importRecord.id,
                        successCount: importRecord.successCount ?? 0,
                        failedCount: importRecord.failedCount ?? 0,
                        totalRecords: importRecord.totalRecords ?? 0,
                        message: `Import: ${importRecord.fileName}. Success: ${importRecord.successCount ?? 0}, Failed: ${importRecord.failedCount ?? 0}`,
                      })
                    }
                  >
                    <TableCell>{importRecord.fileName}</TableCell>
                    <TableCell>{importRecord.totalRecords}</TableCell>
                    <TableCell>
                      <Chip
                        label={importRecord.successCount}
                        color="success"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={importRecord.failedCount}
                        color="error"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={importRecord.importStatus}
                        color={
                          importRecord.importStatus === "completed"
                            ? "success"
                            : importRecord.importStatus === "failed"
                              ? "error"
                              : "warning"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(importRecord.created).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Dialog */}
      <Dialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Attendee</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name *"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password *"
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Shop Name *"
                value={formData.shopName}
                onChange={(e) =>
                  setFormData({ ...formData, shopName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Shop Location"
                value={formData.shopLocation}
                onChange={(e) =>
                  setFormData({ ...formData, shopLocation: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Shop Contact"
                value={formData.shopContact}
                onChange={(e) =>
                  setFormData({ ...formData, shopContact: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialog}
        onClose={() => setUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Attendees CSV</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Required columns: {REQUIRED_CSV_COLUMNS.join(", ")}. Optional:{" "}
              {OPTIONAL_CSV_COLUMNS.join(", ")}
            </Typography>
            <Typography
              variant="caption"
              component="pre"
              sx={{
                display: "block",
                p: 1.5,
                bgcolor: "grey.100",
                borderRadius: 1,
                fontFamily: "monospace",
                mb: 2,
              }}
            >
              {SAMPLE_CSV_HEADER}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadSampleCsv}
              sx={{ mb: 2 }}
            >
              Download Sample CSV
            </Button>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: "none" }}
              id="csv-upload-input"
            />
            <label htmlFor="csv-upload-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                fullWidth
              >
                {csvFile ? csvFile.name : "Select CSV File"}
              </Button>
            </label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCsvUpload}
            variant="contained"
            disabled={!csvText}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Status Modal */}
      <Dialog
        open={uploadStatusModal.open}
        onClose={() =>
          setUploadStatusModal((s) => ({ ...s, open: false }))
        }
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload Report</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {uploadStatusModal.message}
          </Alert>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <Chip
              label={`Total: ${uploadStatusModal.totalRecords}`}
              color="default"
              variant="outlined"
            />
            <Chip
              label={`Success: ${uploadStatusModal.successCount}`}
              color="success"
              variant="outlined"
            />
            <Chip
              label={`Failed: ${uploadStatusModal.failedCount}`}
              color="error"
              variant="outlined"
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Download lists:
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadSuccessList}
              disabled={
                !uploadStatusModal.importId ||
                uploadStatusModal.successCount === 0
              }
            >
              Download Success List
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadFailedList}
              disabled={
                !uploadStatusModal.importId ||
                uploadStatusModal.failedCount === 0
              }
            >
              Download Failed List
            </Button>
          </Box>
          {failedRowsDetail.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                Failed rows (reason for failure):
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Shop</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {failedRowsDetail.map((r, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{r.row}</TableCell>
                        <TableCell>{r.email}</TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>{r.shop_name}</TableCell>
                        <TableCell sx={{ color: "error.main", maxWidth: 200 }} title={r.error}>
                          {r.error}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          {uploadStatusModal.successCount > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Do you want to send email to all successful upload data?
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EmailIcon />}
                  onClick={handleConfirmSendEmailToUploaded}
                  disabled={!templatesData?.getEmailTemplates?.length}
                >
                  Yes, send email
                </Button>
                <Button variant="outlined" size="small">
                  No
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setUploadStatusModal((s) => ({ ...s, open: false }))
            }
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <EmailPreviewDialog
        open={emailPreviewOpen}
        onClose={() => setEmailPreviewOpen(false)}
        templateType="attendee_welcome"
        template={attendeeWelcomeTemplate}
        selectedRows={selectedAttendeeRows}
        onSend={handleSendEmailsFromPreview}
        title="Attendee Welcome Email - Preview"
      />
      <EmailPreviewDialog
        open={emailFromUploadOpen}
        onClose={() => setEmailFromUploadOpen(false)}
        templateType="attendee_welcome"
        template={attendeeWelcomeTemplate}
        selectedRows={uploadSuccessRowsForEmail}
        onSend={handleSendEmailsFromUploadPreview}
        onSuccess={(msg) => {
          setNotification({ open: true, message: msg, severity: "success" });
          refetchAttendees();
          refetchImports();
        }}
        title="Attendee Welcome Email - Preview (Uploaded Users)"
      />

      {/* Notification */}
      {notification.open && (
        <Alert
          severity={notification.severity}
          onClose={() => setNotification({ ...notification, open: false })}
          sx={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}
        >
          {notification.message}
        </Alert>
      )}
    </Box>
  );
};

export default AttendeeManagementPage;
